(() => {
  if (customElements.get('bf-discount-popup')) return;

  class BfDiscountPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      this.code = this.querySelector('[data-bf-code]');
      this.status = this.querySelector('[data-bf-status]');
      this.startsAt = Number(this.dataset.startsAt);
      this.endsAt = Number(this.dataset.endsAt);
      this.delay = Number(this.dataset.delay);
      this.maxViews = Number(this.dataset.maxViews);
      this.viewerKey = this.dataset.viewerKey || 'guest';
      this.editor = this.dataset.editor === 'true';
      if (!this.dialog || (this.code && !this.status) || typeof this.dialog.showModal !== 'function') return;
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(this.dataset.campaign || '') || this.dataset.campaign.length > 80) return;
      if (!Number.isSafeInteger(this.startsAt) || this.startsAt <= 0 || !Number.isSafeInteger(this.endsAt) || this.endsAt <= this.startsAt) return;
      if (!Number.isInteger(this.delay) || this.delay < 0 || this.delay > 10000 || (this.code && !this.code.value.trim())) return;
      if (!Number.isInteger(this.maxViews) || this.maxViews < 1 || this.maxViews > 5) return;
      this.storageKey = `tpg-bf-popup:${this.dataset.campaign}`;
      this.viewerStorageKey = `${this.storageKey}:viewer:${this.viewerKey}`;
      this.viewCountKey = `${this.viewerStorageKey}:views`;
      this.visitKey = `${this.viewerStorageKey}:visit`;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-bf-close]')) this.close();
        if (event.target.closest('[data-bf-copy]')) this.copy();
        if (event.target.closest('[data-bf-preview]') && this.editor) this.open();
        if (event.target === this.dialog) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
        }
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => this.release(), options);
      document.addEventListener('visibilitychange', () => {
        if (!this.editor && !this.active()) this.close();
        else if (!this.editor && !this.dialog.open) this.schedule();
      }, options);
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target === this || event.target.contains(this)) this.disconnect();
      }, options);
      window.addEventListener('pagehide', () => { this.suspended = true; this.stopTimers(); this.close(false); }, options);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) { this.suspended = true; this.stopTimers(); this.close(false); }
      }, options);
      if (!this.editor) this.schedule();
    }

    disconnectedCallback() {
      this.disconnect();
    }

    disconnect() {
      this.stopTimers();
      this.close(false);
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    stopTimers() {
      clearTimeout(this.timer);
      clearTimeout(this.expiryTimer);
    }

    active() {
      return Date.now() >= this.startsAt && Date.now() < this.endsAt;
    }

    schedule() {
      if (!this.isConnected || this.suspended || this.seenThisVisit() || this.viewCount() >= this.maxViews) return;
      const untilStart = this.startsAt - Date.now();
      if (untilStart > 0) {
        this.timer = setTimeout(() => this.schedule(), Math.min(untilStart, 2147483647));
        return;
      }
      if (this.active()) this.timer = setTimeout(() => this.attempt(), this.delay);
    }

    seen() {
      return this.shownThisVisit || this.seenThisVisit() || this.viewCount() >= this.maxViews;
    }

    readCount(store) {
      try {
        const value = window[store].getItem(this.viewCountKey);
        const count = Number(value);
        return Number.isInteger(count) && count >= 0 ? count : 0;
      } catch (_) {
        return 0;
      }
    }

    viewCount() {
      let count = Math.max(this.readCount('localStorage'), this.readCount('sessionStorage'));
      for (const store of ['localStorage', 'sessionStorage']) {
        try {
          if (window[store].getItem(this.storageKey) === 'shown') count = Math.max(count, 1);
        } catch (_) {}
      }
      return count;
    }

    seenThisVisit() {
      if (this.shownThisVisit) return true;
      try { return window.sessionStorage.getItem(this.visitKey) === 'shown'; } catch (_) { return false; }
    }

    rememberView() {
      const count = this.viewCount() + 1;
      this.shownThisVisit = true;
      try { window.localStorage.setItem(this.viewCountKey, String(count)); } catch (_) {}
      try {
        window.sessionStorage.setItem(this.viewCountKey, String(count));
        window.sessionStorage.setItem(this.visitKey, 'shown');
      } catch (_) {}
    }

    visible() {
      if (!this.dialog.open) return false;
      const bounds = this.dialog.getBoundingClientRect();
      const style = getComputedStyle(this.dialog);
      return bounds.width > 0 && bounds.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
    }

    blocked() {
      if (document.hidden) return true;
      const selector = 'dialog[open], modal-dialog[open], bulk-modal[open], cart-drawer.active, .drawer.active, menu-drawer details[open], header-drawer details[open], [role="dialog"][aria-modal="true"]';
      const otherOpen = Array.from(document.querySelectorAll(selector)).some((element) => {
        if (element === this.dialog || this.dialog.contains(element)) return false;
        if (element.closest('details:not([open])')) return false;
        return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
      });
      return otherOpen || Array.from(document.body.classList).some((name) => name === 'overflow-hidden' || name.startsWith('overflow-hidden-'));
    }

    attempt() {
      if (!this.isConnected || this.suspended || this.seen()) return;
      if (!this.active()) { this.schedule(); return; }
      if (this.blocked()) { this.timer = setTimeout(() => this.attempt(), 250); return; }
      this.open();
    }

    open() {
      if (!this.isConnected || this.suspended || this.dialog.open || this.blocked()) return;
      if (!this.editor && (!this.active() || this.seen())) return;
      this.previousFocus = document.activeElement;
      this.scrollPosition = [window.scrollX, window.scrollY];
      this.restore = true;
      try { this.dialog.showModal(); } catch (_) { return; }
      if (!this.dialog.open) return;
      if (!this.visible()) { this.close(false); return; }
      if (!document.documentElement.classList.contains('bf-popup-scroll-locked')) {
        document.documentElement.classList.add('bf-popup-scroll-locked');
        this.ownsScrollLock = true;
      }
      if (!this.editor) { this.rememberView(); this.watchExpiry(); }
    }

    watchExpiry() {
      const remaining = this.endsAt - Date.now();
      if (remaining <= 0) { this.close(); return; }
      this.expiryTimer = setTimeout(() => this.watchExpiry(), Math.min(remaining, 2147483647));
    }

    close(restore = true) {
      clearTimeout(this.expiryTimer);
      this.restore = restore;
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.release();
    }

    release() {
      if (this.ownsScrollLock) document.documentElement.classList.remove('bf-popup-scroll-locked');
      this.ownsScrollLock = false;
      if (this.restore && this.previousFocus && this.previousFocus.isConnected) this.previousFocus.focus({ preventScroll: true });
      if (this.restore && this.scrollPosition) window.scrollTo(...this.scrollPosition);
      this.previousFocus = null;
      this.scrollPosition = null;
      if (this.status) this.status.textContent = '';
    }

    async copy() {
      if (!this.code || !this.status) return;
      try {
        await navigator.clipboard.writeText(this.code.value);
        if (this.isConnected && this.dialog.open) this.status.textContent = this.dataset.copied;
      } catch (_) {
        if (!this.isConnected || !this.dialog.open) return;
        this.code.focus({ preventScroll: true });
        this.code.select();
        this.status.textContent = this.dataset.copyFallback;
      }
    }
  }

  customElements.define('bf-discount-popup', BfDiscountPopup);
})();

